import { useEffect, useState } from 'react';
import { ChevronRight, Folder, Home } from '../../../icons';
import { Button, Checkbox, FormField, Select } from '../../../components/shared';
import { listFolderChildren, foldersOf, type NodePublic } from '../../../api/workspace';
import { listResources } from '../../../api/reservation';
import { getWeatherGridSettings } from '../../../api/weather';
import type { WidgetSettings } from '../../../store/dashboardStore';
import { QUICK_LINKS, DEFAULT_QUICK_LINKS } from './quick-links';
import { useMenuEnabledFn } from '../../../lib/menu-gate';

/**
 * 위젯 «고유» 설정 — 판 5 의 위젯 셋만 갖는다 (2026-09-09 · 시안 화면 C 의 아래 절).
 *
 * ★★★ 시안 화면 C 는 「이 위젯만의 설정」 자리를 비워 두고 «위젯이 생기면 채운다» 고 했다
 *     (판 4a 의 §10). 판 5 에서 **처음으로 채울 것이 생겼다** — 포토월의 폴더 · 빠른 실행의
 *     바로가기 · 회의실 현황의 분류. 셋 다 **없으면 위젯이 보여 줄 것을 모르는** 값이라,
 *     「켜도 아무 일 없는 스위치」가 아니다.
 * ★ 값은 배치(`BoardWidget.settings`)에 담긴다 — 같은 위젯을 두 자리에 놓으면 서로 다른 폴더를
 *   볼 수 있다.
 */

interface Props {
  widgetKey: string;
  value: WidgetSettings;
  onChange: (patch: WidgetSettings) => void;
}

export function WidgetOwnSettings({ widgetKey, value, onChange }: Props) {
  if (widgetKey === 'photo-wall') return <PhotoFolderPicker value={value} onChange={onChange} />;
  if (widgetKey === 'quick-launch') return <QuickLinkPicker value={value} onChange={onChange} />;
  if (widgetKey === 'room-status') return <RoomCategoryPicker value={value} onChange={onChange} />;
  if (widgetKey === 'calendar') return <CalendarDayClickPicker value={value} onChange={onChange} />;
  if (widgetKey === 'weather') return <WeatherPlacePicker value={value} onChange={onChange} />;
  return null;
}

/** 이 위젯이 고유 설정을 갖는가 — 설정 창이 절 제목을 그릴지 판정한다. */
export function hasOwnSettings(widgetKey: string): boolean {
  return widgetKey === 'photo-wall' || widgetKey === 'quick-launch' || widgetKey === 'room-status'
    || widgetKey === 'calendar' || widgetKey === 'weather';
}

/* ── 날씨 — 어디 날씨 ───────────────────────────────────────────────────── */

/**
 * 「어디 날씨」 (2026-09-21 · 시안 v4).
 *
 * ★★★ 기본은 **내 사업장, 없으면 회사**다 — 그 판단은 **서버**가 한다(화면은 내 근무지를 모른다).
 * ★★ 사업장 하나를 콕 집는 것은 **사업장 목록을 읽을 수 있는 사람만** 할 수 있다.
 *     그 권한이 없으면 서버가 목록을 \`null\` 로 주고, 여기서는 그 고르개를 **아예 그리지 않는다**
 *     (날씨 때문에 사업장 목록이 새면 안 된다 — 응답에 주소를 안 싣기로 한 것과 같은 결).
 */
function WeatherPlacePicker({ value, onChange }: { value: WidgetSettings; onChange: (p: WidgetSettings) => void }) {
  const [sites, setSites] = useState<Array<{ id: string; name: string }> | null>(null);
  const [loaded, setLoaded] = useState(false);
  const place = value.weatherPlace ?? 'mine';

  useEffect(() => {
    let alive = true;
    getWeatherGridSettings()
      .then((r) => { if (alive) setSites(r.worksites?.map((w) => ({ id: w.id, name: w.name })) ?? null); })
      .catch(() => { /* 옛 서버 · 권한 없음 — 고르개를 안 그린다 */ })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const options = [
    { value: 'mine', label: '내 사업장 (없으면 본사)' },
    { value: 'company', label: '본사' },
    ...(sites ?? []).map((s) => ({ value: s.id, label: s.name })),
  ];
  // 저장된 값이 목록에 없으면(권한이 없어 목록을 못 받았거나 사업장이 지워졌다) 그 값을 잃지 않게 한 줄 남긴다.
  if (!options.some((o) => o.value === place)) options.push({ value: place, label: '고른 사업장' });

  return (
    <FormField label="어디 날씨" hint={loaded && sites === null ? '사업장을 고르려면 사업장 목록을 볼 권한이 필요합니다' : '기본은 소속 사업장이고, 소속이 없으면 본사 주소를 씁니다'}>
      <Select
        value={place}
        options={options}
        onChange={(v) => onChange({ weatherPlace: String(v) })}
      />
    </FormField>
  );
}

/* ── 캘린더 — 날짜를 누르면 ──────────────────────────────────────────────── */

/**
 * 「날짜를 누르면」 (2026-09-16 · 사용자 요청 · 시안에는 없던 새 칸).
 *
 * ★★★ 기본은 **그 아래에 펼치기**지만, **캘린더 화면으로 가던 길을 없애지 않는다** —
 *     고르개에 `'navigate'` 를 그대로 두고, 펼친 칸 발에도 「캘린더에서 보기 →」를 남긴다.
 * ★ 서버를 새로 부르지 않는다 — 위젯이 이미 그 달 일정을 받아 점을 찍고 있다.
 */
const DAY_CLICK_OPTIONS = [
  { value: 'expand', label: '그 아래에 그날 일정 펼치기' },
  { value: 'navigate', label: '캘린더 화면으로 이동' },
  { value: 'none', label: '아무 일 없음' },
] as const;

function CalendarDayClickPicker({ value, onChange }: { value: WidgetSettings; onChange: (p: WidgetSettings) => void }) {
  const mode = value.dayClick ?? 'expand';
  return (
    <FormField label="날짜를 누르면" hint="달력에서 날짜를 눌렀을 때 할 일을 고릅니다">
      {/* ★ 공용 `Select` 는 네이티브 `<select>` 가 아니다 — `Combobox` 껍데기라 **`options` 로 넘기고**
             `onChange` 는 **값 자체**를 준다(`e.target.value` 가 아니다). 바로 아래 「자원 분류」와 같은 모양.
             `portal` 은 이 창(`.wc-modal__body`)이 `overflow` 를 가져서 켠다 — 안 켜면 목록이 잘린다. */}
      <Select
        value={mode}
        onChange={(v) => onChange({ dayClick: v as WidgetSettings['dayClick'] })}
        options={DAY_CLICK_OPTIONS}
        ariaLabel="날짜를 누르면"
        portal
      />
    </FormField>
  );
}

/* ── 포토월 — 워크스페이스 폴더 고르기 ──────────────────────────────────── */

interface Crumb { id: string; name: string }

/**
 * ★★ 폴더는 **한 층씩 들어가며** 고른다 — 회사 폴더가 깊으면 목록 하나로는 못 찾는다.
 * ★ 「여기로 정하기」는 **지금 서 있는 폴더**를 고른다(뿌리는 못 고른다 — 그러면 전체가 된다).
 */
function PhotoFolderPicker({ value, onChange }: { value: WidgetSettings; onChange: (p: WidgetSettings) => void }) {
  const [path, setPath] = useState<Crumb[]>([]);
  const [rows, setRows] = useState<NodePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const here = path[path.length - 1];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await listFolderChildren(here?.id ?? 'root', { foldersOnly: true, limit: 100 });
        if (!cancelled) setRows(foldersOf(res).filter((n) => n.type === 'folder'));
      } catch (e) {
        if (!cancelled) setError((e as Error)?.message ?? '폴더를 불러오지 못했습니다');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [here?.id]);

  return (
    <FormField label="사진 폴더" hint="고른 폴더의 이미지가 최신 순으로 걸립니다. 폴더를 고르기 전에는 아무것도 조회하지 않습니다.">
      <div className="dws-folder">
        <div className="dws-folder__crumb">
          <button type="button" className="dws-folder__crumbi" onClick={() => setPath([])}>
            <Home size={13} aria-hidden />내 파일
          </button>
          {path.map((c, i) => (
            <button
              type="button" key={c.id} className="dws-folder__crumbi"
              onClick={() => setPath(path.slice(0, i + 1))}
            >
              <ChevronRight size={12} aria-hidden />{c.name}
            </button>
          ))}
        </div>

        <div className="dws-folder__list">
          {loading && <p className="dws-folder__note">불러오는 중…</p>}
          {error && <p className="dws-folder__note" role="alert">{error}</p>}
          {!loading && !error && rows.length === 0 && <p className="dws-folder__note">하위 폴더가 없습니다</p>}
          {rows.map((f) => (
            <button
              type="button" key={f.id}
              className={`dws-folder__row${value.photoFolderId === f.id ? ' is-on' : ''}`}
              onClick={() => setPath([...path, { id: f.id, name: f.name }])}
            >
              <Folder size={14} aria-hidden />
              <span className="dws-folder__n">{f.name}</span>
              <ChevronRight size={13} aria-hidden />
            </button>
          ))}
        </div>

        <div className="dws-folder__foot">
          <span className="dws-folder__picked">
            {value.photoFolderId
              ? <>지금 고른 폴더 — <b>{value.photoFolderName ?? value.photoFolderId}</b></>
              : '아직 고르지 않았습니다'}
          </span>
          <span className="dws-folder__acts">
            {value.photoFolderId && (
              <Button variant="ghost" size="sm"
                onClick={() => onChange({ photoFolderId: undefined, photoFolderName: undefined })}
              >
                고른 것 지우기
              </Button>
            )}
            <Button
              variant="secondary" size="sm" disabled={!here}
              onClick={() => here && onChange({ photoFolderId: here.id, photoFolderName: here.name })}
            >
              {here ? `「${here.name}」로 정하기` : '폴더로 들어가 고르세요'}
            </Button>
          </span>
        </div>
      </div>
    </FormField>
  );
}

/* ── 빠른 실행 — 바로가기 고르기 ────────────────────────────────────────── */

/**
 * ★★★ **끈 메뉴의 바로가기는 목록에 없다** — 고를 수 있는데 안 보이면 「왜 안 뜨지」가 된다.
 * ★ 순서는 카탈로그 차례를 따른다(고른 차례로 두면 지웠다 다시 고를 때마다 자리가 흔들린다).
 */
function QuickLinkPicker({ value, onChange }: { value: WidgetSettings; onChange: (p: WidgetSettings) => void }) {
  const menuEnabled = useMenuEnabledFn();
  const picked = new Set(value.quickLinks ?? DEFAULT_QUICK_LINKS);
  const catalog = QUICK_LINKS.filter((d) => !d.menuKey || menuEnabled(d.menuKey));

  const toggle = (key: string) => {
    const next = catalog.filter((d) => (d.key === key ? !picked.has(key) : picked.has(d.key))).map((d) => d.key);
    onChange({ quickLinks: next });
  };

  return (
    <FormField label="바로가기" hint="자주 하는 일만 남기세요. 관리자가 끈 메뉴는 목록에 없습니다.">
      <div className="dws-quick">
        {catalog.map((d) => (
          <Checkbox
            key={d.key}
            checked={picked.has(d.key)}
            onChange={() => toggle(d.key)}
            label={d.label}
          />
        ))}
      </div>
    </FormField>
  );
}

/* ── 회의실 현황 — 자원 분류 고르기 ─────────────────────────────────────── */

/** 「전체」의 자리표 — 빈 문자열은 고르개가 «안 고름» 으로 읽는다. */
const ALL = '__all__';

function RoomCategoryPicker({ value, onChange }: { value: WidgetSettings; onChange: (p: WidgetSettings) => void }) {
  const [cats, setCats] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await listResources();
        if (cancelled) return;
        setCats([...new Set(rows.map((r) => r.category).filter((c) => typeof c === 'string' && c.length > 0))]);
      } catch {
        /* 못 받아도 창은 선다 — 「전체」로 두면 된다. */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <FormField label="자원 분류" hint="비워 두면 등록된 자원을 전부 봅니다.">
      {/* ⚠ 빈 문자열을 값으로 쓰면 고르개가 「안 고른 상태」로 읽어 «전체» 라는 글자가 안 뜬다. */}
      <Select
        value={value.resourceCategory ?? ALL}
        onChange={(v) => onChange({ resourceCategory: v === ALL ? undefined : v })}
        options={[{ value: ALL, label: '전체' }, ...cats.map((c) => ({ value: c, label: c }))]}
        ariaLabel="자원 분류"
        portal
      />
    </FormField>
  );
}
